import type { InventoryOccupancyMeasurement } from "../api/rest";

/**
 * Rack elevation layout for the print-only 3D snapshot (ADR 163). Pure geometry
 * planning — no three.js, no React — so the placement/split rules are unit
 * tested; {@link ../scene/renderRackSnapshot} turns a {@link RackLayout} into a
 * PNG.
 *
 * Fed by the run's {@link InventoryOccupancyMeasurement}s (the same sensor-
 * measured set the occupation section shows, ADR 162) so the rack's occupancy %
 * and heights never disagree with that section. Pallets are laid out on a clean
 * bay×level grid read from the address grammar, NOT the real (mirrored) floor
 * polygons: a printed elevation reads far better than a perspective of scattered
 * world coordinates, and the grammar already carries everything the grid needs.
 */

export interface ParsedRackAddress {
  rua: string;
  level: number;
  bay: number;
  pos: string;
}

// {Rua}{Nível:02}-{bay}{pos} (ADR 155): JY02-14B → JY / 2 / 14 / "B". The rua
// run is lazy so the level digits are the trailing number of the token, and the
// position letters are optional. Off-grammar addresses (AA-01-01) return null.
const RACK_ADDR = /^([A-Za-z]+?)(\d+)-(\d+)([A-Za-z]*)$/;

export function parseRackAddress(address: string): ParsedRackAddress | null {
  const m = RACK_ADDR.exec(address.trim());
  if (!m) return null;
  return { rua: m[1].toUpperCase(), level: Number(m[2]), bay: Number(m[3]), pos: m[4].toUpperCase() };
}

export interface RackPlacement {
  /** Storage-location name (the address). */
  address: string;
  /** Occupancy the kit reported for the bin, 0–100 (ADR 154), trusted verbatim. */
  occupancyPct: number;
  level: number;
  bay: number;
  pos: string;
  /** Pallet load height, metres (sensor-measured), clamped to the clear height. */
  loadM: number;
  /** Bin clear height, metres — the slot frame. */
  clearM: number;
}

export interface RackLayout {
  /** storage_location.side when the images are split by side; null otherwise. */
  side: string | null;
  placements: RackPlacement[];
  bays: number[];
  levels: number[];
}

/** Sensor-measured bins placed on a bay×level grid — the measured set the
 *  occupation section shows (ADR 162); a run counts hundreds of bins, which no
 *  legible page could hold. Bins we cannot place — off-grammar address, or no
 *  measured heights — are dropped rather than guessed onto the grid. */
export function buildRackLayout(
  measurements: InventoryOccupancyMeasurement[],
  side: string | null = null,
): RackLayout {
  const placements: RackPlacement[] = [];
  for (const m of measurements) {
    if (m.loadHeightMm == null || m.binHeightMm == null) continue;
    const parsed = parseRackAddress(m.address);
    if (parsed == null) continue;
    const clearM = m.binHeightMm / 1000;
    const loadM = Math.min(m.loadHeightMm / 1000, clearM);
    placements.push({
      address: m.address,
      occupancyPct: m.occupancyPct,
      level: m.level ?? parsed.level,
      bay: parsed.bay,
      pos: parsed.pos,
      loadM,
      clearM,
    });
  }
  placements.sort((a, b) => a.level - b.level || a.bay - b.bay || a.pos.localeCompare(b.pos));
  return {
    side,
    placements,
    bays: [...new Set(placements.map((p) => p.bay))].sort((a, b) => a - b),
    levels: [...new Set(placements.map((p) => p.level))].sort((a, b) => a - b),
  };
}

/** Halve a wide layout's bay range into two contiguous images so labels stay
 *  legible across a long aisle. */
function splitByBayHalves(layout: RackLayout): RackLayout[] {
  const mid = layout.bays[Math.ceil(layout.bays.length / 2)];
  const near = layout.placements.filter((p) => p.bay < mid);
  const far = layout.placements.filter((p) => p.bay >= mid);
  return [
    { ...layout, placements: near, bays: layout.bays.filter((b) => b < mid), levels: [...new Set(near.map((p) => p.level))].sort((a, b) => a - b) },
    { ...layout, placements: far, bays: layout.bays.filter((b) => b >= mid), levels: [...new Set(far.map((p) => p.level))].sort((a, b) => a - b) },
  ];
}

/**
 * Plan the print images for a run's measured bins: one per storage-location side
 * (the two facing rack rows of an aisle) when the master carries a side that
 * partitions the bins into two, else a single view — then halve any view too
 * wide to stay legible. `sideOf` maps an address to its side ("" / null = no side
 * known). Empty views are dropped.
 */
export function planRackImages(
  measurements: InventoryOccupancyMeasurement[],
  sideOf: (address: string) => string | null,
  maxBaysPerImage = 8,
): RackLayout[] {
  const bySide = new Map<string, InventoryOccupancyMeasurement[]>();
  for (const m of measurements) {
    const key = sideOf(m.address) ?? "";
    (bySide.get(key) ?? bySide.set(key, []).get(key)!).push(m);
  }
  const namedSides = [...bySide.keys()].filter((k) => k !== "");
  const base: RackLayout[] = namedSides.length >= 2
    ? namedSides.sort().map((s) => buildRackLayout(bySide.get(s)!, s))
    : [buildRackLayout(measurements, null)];

  const out: RackLayout[] = [];
  for (const layout of base) {
    if (layout.placements.length === 0) continue;
    if (layout.bays.length > maxBaysPerImage) out.push(...splitByBayHalves(layout));
    else out.push(layout);
  }
  return out;
}
